/* eslint-disable no-console */

/**
 * Printer Monitoring Service
 * Frontend-only monitoring for printer status and health
 * kware: خدمة مراقبة الطابعات Frontend-only بدون الحاجة لـ Backend
 */
class PrinterMonitoringService {
  constructor() {
    this.monitors = new Map();
    this.alertHandlers = [];
    this.monitoringInterval = 30000; // 30 seconds
    this.statusHistory = new Map();
    this.maxHistoryEntries = 100;
  }

  /**
   * Start monitoring a printer
   */
  startMonitoring(printerId, connection) {
    if (this.monitors.has(printerId)) {
      this.stopMonitoring(printerId);
    }

    const monitor = {
      printerId,
      connection,
      startedAt: new Date(),
      lastCheck: null,
      status: 'starting',
      alerts: [],
      stats: {
        uptime: 0,
        totalChecks: 0,
        successfulChecks: 0,
        errorCount: 0,
        lastError: null
      }
    };

    // Start monitoring interval
    monitor.intervalId = setInterval(async () => {
      await this.performStatusCheck(printerId);
    }, this.monitoringInterval);

    this.monitors.set(printerId, monitor);

    // Perform initial check
    setTimeout(() => this.performStatusCheck(printerId), 1000);

    console.log(`Started monitoring printer: ${printerId}`);
    return monitor;
  }

  /**
   * Stop monitoring a printer
   */
  stopMonitoring(printerId) {
    const monitor = this.monitors.get(printerId);
    if (monitor) {
      clearInterval(monitor.intervalId);
      this.monitors.delete(printerId);
      console.log(`Stopped monitoring printer: ${printerId}`);
    }
  }

  /**
   * Perform status check for a printer
   */
  async performStatusCheck(printerId) {
    const monitor = this.monitors.get(printerId);
    if (!monitor) return;

    monitor.stats.totalChecks++;
    monitor.lastCheck = new Date();

    try {
      const status = await this.checkPrinterStatus(monitor.connection);

      // Update monitor status
      monitor.status = status.overall;
      monitor.lastStatus = status;
      monitor.stats.successfulChecks++;

      // Update uptime
      monitor.stats.uptime = Date.now() - monitor.startedAt.getTime();

      // Store status history
      this.storeStatusHistory(printerId, status);

      // Check for alerts
      this.checkForAlerts(printerId, status);

      // Trigger status update callbacks
      this.notifyStatusUpdate(printerId, status);
    } catch (error) {
      console.error(`Status check failed for printer ${printerId}:`, error);

      monitor.status = 'error';
      monitor.stats.errorCount++;
      monitor.stats.lastError = {
        message: error.message,
        timestamp: new Date()
      };

      // Trigger error callbacks
      this.notifyStatusUpdate(printerId, {
        overall: 'error',
        error: error.message,
        timestamp: new Date()
      });
    }
  }

  /**
   * Check printer status based on connection type
   */
  async checkPrinterStatus(connection) {
    const status = {
      overall: 'unknown',
      connectivity: 'unknown',
      supplies: 'unknown',
      errors: [],
      warnings: [],
      timestamp: new Date()
    };

    if (!connection || connection.virtual) {
      // Virtual connections (file-based, etc.)
      status.overall = 'ready';
      status.connectivity = 'virtual';
      status.supplies = 'n/a';
      return status;
    }

    // Check connectivity
    status.connectivity = await this.checkConnectivity(connection);

    if (status.connectivity === 'connected') {
      // Try to get printer status if possible
      try {
        const printerStatus = await this.queryPrinterStatus(connection);
        Object.assign(status, printerStatus);
      } catch (error) {
        status.warnings.push('Could not query detailed printer status');
      }

      status.overall = status.errors.length > 0 ? 'error' : 'ready';
    } else {
      status.overall = 'disconnected';
      status.errors.push('Printer not connected');
    }

    return status;
  }

  /**
   * Check basic connectivity
   */
  async checkConnectivity(connection) {
    try {
      if (connection.type === 'usb') {
        return connection.device && connection.device.opened ? 'connected' : 'disconnected';
      } else if (connection.type === 'serial') {
        return connection.port && connection.port.readable ? 'connected' : 'disconnected';
      }

      return 'unknown';
    } catch (error) {
      return 'error';
    }
  }

  /**
   * Query detailed printer status (if supported)
   */
  async queryPrinterStatus(connection) {
    const status = {
      supplies: 'unknown',
      errors: [],
      warnings: []
    };

    try {
      // Try to send a status query command
      // This is printer-specific and may not always work
      const statusCommand = this.getStatusCommand(connection.printerType);

      if (statusCommand) {
        // Note: This would require a response reading mechanism
        // For now, we'll simulate basic status
        status.supplies = 'good'; // Assume good if no errors
      }
    } catch (error) {
      status.warnings.push('Could not query printer supplies');
    }

    return status;
  }

  /**
   * Get status command for different printer types
   */
  getStatusCommand(printerType) {
    const commands = {
      'ZEBRA_ZPL': '~HS', // Host Status
      'ESC_POS': '\x10\x04\x01', // Status inquiry
      'BROTHER_PTOUCH': null // No standard command
    };

    return commands[printerType] || null;
  }

  /**
   * Store status history
   */
  storeStatusHistory(printerId, status) {
    if (!this.statusHistory.has(printerId)) {
      this.statusHistory.set(printerId, []);
    }

    const history = this.statusHistory.get(printerId);
    history.unshift(status);

    // Keep only recent entries
    if (history.length > this.maxHistoryEntries) {
      history.splice(this.maxHistoryEntries);
    }
  }

  /**
   * Check for alerts based on status
   */
  checkForAlerts(printerId, status) {
    const monitor = this.monitors.get(printerId);
    if (!monitor) return;

    const alerts = [];

    // Connectivity alerts
    if (status.overall === 'disconnected') {
      alerts.push({
        id: `${printerId}-disconnected`,
        type: 'error',
        message: 'Printer disconnected',
        timestamp: new Date()
      });
    }

    // Error alerts
    if (status.errors.length > 0) {
      status.errors.forEach((error, index) => {
        alerts.push({
          id: `${printerId}-error-${index}`,
          type: 'error',
          message: error,
          timestamp: new Date()
        });
      });
    }

    // Warning alerts
    if (status.warnings.length > 0) {
      status.warnings.forEach((warning, index) => {
        alerts.push({
          id: `${printerId}-warning-${index}`,
          type: 'warning',
          message: warning,
          timestamp: new Date()
        });
      });
    }

    // Update monitor alerts
    monitor.alerts = alerts;

    // Trigger alert handlers
    alerts.forEach(alert => {
      this.triggerAlert(printerId, alert);
    });
  }

  /**
   * Trigger alert handlers
   */
  triggerAlert(printerId, alert) {
    this.alertHandlers.forEach(handler => {
      try {
        handler(printerId, alert);
      } catch (error) {
        console.error('Alert handler error:', error);
      }
    });
  }

  /**
   * Add alert handler
   */
  addAlertHandler(handler) {
    this.alertHandlers.push(handler);
  }

  /**
   * Remove alert handler
   */
  removeAlertHandler(handler) {
    const index = this.alertHandlers.indexOf(handler);
    if (index > -1) {
      this.alertHandlers.splice(index, 1);
    }
  }

  /**
   * Notify status update
   */
  notifyStatusUpdate(printerId, status) {
    // Emit custom event for status updates
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('printerStatusUpdate', {
        detail: { printerId, status }
      }));
    }
  }

  /**
   * Get current status for a printer
   */
  getPrinterStatus(printerId) {
    const monitor = this.monitors.get(printerId);
    return monitor ? {
      printerId,
      status: monitor.status,
      lastCheck: monitor.lastCheck,
      lastStatus: monitor.lastStatus,
      stats: monitor.stats,
      alerts: monitor.alerts
    } : null;
  }

  /**
   * Get status for all monitored printers
   */
  getAllPrinterStatuses() {
    const statuses = {};

    this.monitors.forEach((monitor, printerId) => {
      statuses[printerId] = this.getPrinterStatus(printerId);
    });

    return statuses;
  }

  /**
   * Get status history for a printer
   */
  getStatusHistory(printerId, limit = 10) {
    const history = this.statusHistory.get(printerId) || [];
    return history.slice(0, limit);
  }

  /**
   * Get monitoring statistics
   */
  getMonitoringStats() {
    const stats = {
      totalPrinters: this.monitors.size,
      activePrinters: 0,
      errorPrinters: 0,
      disconnectedPrinters: 0,
      totalAlerts: 0
    };

    this.monitors.forEach(monitor => {
      switch (monitor.status) {
        case 'ready':
          stats.activePrinters++;
          break;
        case 'error':
          stats.errorPrinters++;
          break;
        case 'disconnected':
          stats.disconnectedPrinters++;
          break;
      }

      stats.totalAlerts += monitor.alerts.length;
    });

    return stats;
  }

  /**
   * Test printer connectivity
   */
  async testPrinterConnectivity(printerId) {
    const monitor = this.monitors.get(printerId);
    if (!monitor) {
      throw new Error('Printer not being monitored');
    }

    try {
      const status = await this.checkPrinterStatus(monitor.connection);
      return {
        success: status.connectivity === 'connected',
        status,
        timestamp: new Date()
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        timestamp: new Date()
      };
    }
  }

  /**
   * Set monitoring interval
   */
  setMonitoringInterval(interval) {
    this.monitoringInterval = interval;

    // Restart all monitors with new interval
    const activePrinters = Array.from(this.monitors.keys());
    activePrinters.forEach(printerId => {
      const monitor = this.monitors.get(printerId);
      if (monitor) {
        clearInterval(monitor.intervalId);
        monitor.intervalId = setInterval(async () => {
          await this.performStatusCheck(printerId);
        }, this.monitoringInterval);
      }
    });
  }

  /**
   * Clean up all monitoring
   */
  cleanup() {
    this.monitors.forEach((monitor, printerId) => {
      this.stopMonitoring(printerId);
    });

    this.monitors.clear();
    this.statusHistory.clear();
    this.alertHandlers.length = 0;
  }
}

export default PrinterMonitoringService;
